import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useLazyQuery, useSubscription } from '@apollo/client'
import { Button } from 'react-bootstrap'
import Trans from 'next-translate/Trans'

import styles from '@pages/live-scores/[...matchSlug]/style.module.scss'
import { GET_COMMENTARY_V2 } from '@graphql/match/match.query'
import { commentaryLoading } from '@shared/libs/allLoader'
import { isBottomReached } from '@shared/utils'
import { SUB_MATCH_COMMENTARY_V2 } from '@graphql/match/match.subscription'

const MatchDetailLive = dynamic(() => import('@shared/components/match/matchDetailLive'))
const CommentaryFilter = dynamic(() => import('@shared-components/match/commentaryFilter'))
const Commentary = dynamic(() => import('@shared-components/match/commentary/v2'))
const NoData = dynamic(() => import('@noData'))

function CommentaryV2({ data, matchDetail, liveScoreData, playingXI, isDailyHuntMode }) {
  const router = useRouter()
  const [commentary, setCommentary] = useState(data)
  const isReachedToBottom = useRef(false)
  const isLoadMoreVisible = useRef(true)
  const latestItem = useRef([])
  const updateCommentary = useRef([])
  const payloads = useRef({
    iMatchId: matchDetail?._id,
    nLimit: 15,
    // nSkip: 1,
    eHighLight: router.query?.eHighLight || null,
    nInningNumber: Number(router.query?.nInningNumber) || null,
    sEventId: null
  })

  const [getCommentary, { loading, data: nCommentary }] = useLazyQuery(GET_COMMENTARY_V2)

  useSubscription(SUB_MATCH_COMMENTARY_V2, {
    variables: { input: { iMatchId: matchDetail?._id } },
    skip: typeof window === 'undefined',
    onSubscriptionData: ({ subscriptionData }) => {
      if (subscriptionData?.data?.listMatchCommentariesV3?.length) {
        const c = subscriptionData?.data?.listMatchCommentariesV3?.reverse()
        updateCommentary.current = [...updateCommentary.current, ...c]
        handleSubscriptionData(subscriptionData?.data?.listMatchCommentariesV3)
      }
    }
  })

  useEffect(() => {
    latestItem.current = nCommentary?.listMatchCommentariesV2 || []
    if (latestItem.current?.length) {
      if (isReachedToBottom.current) {
        isReachedToBottom.current = false
        setCommentary([...commentary, ...latestItem.current])
      } else {
        updateCommentary.current = []
        setCommentary(latestItem.current)
      }
      if (!isLoadMoreVisible.current) {
        const id = latestItem.current[latestItem.current.length - 1]?._id
        isBottomReached(id, isReached)
        // isLoadMoreVisible.current = true
      }
    }
  }, [nCommentary])

  function isReached(reacted) {
    if (reacted && !isReachedToBottom.current && latestItem.current?.length === payloads.current?.nLimit) {
      isReachedToBottom.current = true
      payloads.current = {
        ...payloads.current,
        // nSkip: payloads.current.nSkip + 1,
        sEventId: latestItem.current?.[latestItem.current?.length - 1]?.sEventId
      }
      getCommentary({ variables: { input: payloads.current } })
    }
  }

  function handleFilterChange({ eHighLight = null, nInningNumber = 0 }) {
    if (eHighLight || nInningNumber) {
      payloads.current = { ...payloads.current, eHighLight, nInningNumber, sEventId: null }
    } else {
      payloads.current = { ...payloads.current, eHighLight: null, nInningNumber: null, sEventId: null }
    }
    isLoadMoreVisible.current = true
    isReachedToBottom.current = false
    getCommentary({ variables: { input: payloads.current } })
  }

  function handleLoadMore() {
    payloads.current = { ...payloads.current, sEventId: commentary[commentary.length - 1]?.sEventId }
    isReachedToBottom.current = true
    isLoadMoreVisible.current = false
    getCommentary({ variables: { input: payloads.current } })
  }

  function handleSubscriptionData() {
    const isMissed = updateCommentary.current?.find((e) => e?.eCommType === 'm')
    if (isMissed) {
      isReachedToBottom.current = false
      getCommentary({ variables: { input: { ...payloads.current, sEventId: null } } })
    } else if (!payloads.current.eHighLight) {
      let n = [...commentary]
      updateCommentary.current?.forEach((e) => {
        if (e?.eEvent === 'c') {
          if (e?.eOperationType === 'i') {
            if (e?.iBeforeCommId) {
              const index = n.findIndex((x) => x?._id === e?.iBeforeCommId)
              if (index !== -1) {
                n.splice(index, 0, e)
              }
            } else {
              n.unshift(e)
            }
          } else if (e?.eOperationType === 'u') {
            n = n.map((x) => (x._id === e._id ? e : x))
          } else if (e?.eOperationType === 'd') {
            n = n.filter((x) => x._id !== e._id)
          }
        } else {
          if (e?.eCommType === 'd') {
            n = n?.filter((f) => f?.sEventId !== e?.sEventId)
          } else if (e?.eCommType === 'u') {
            n = n?.map((f) => (f?.sEventId === e?.sEventId ? e : f))
          } else {
            n = [e, ...n]
          }
        }
        updateCommentary.current = updateCommentary.current?.filter((f) => f?._id !== e?._id)
      })
      setCommentary(n)
    }
  }
  return (
    <>
      {matchDetail?.sStatusStr === 'live' && matchDetail?.sLiveGameStatusStr !== 'innings break' && (
        <MatchDetailLive styles={styles} matchDetail={matchDetail} liveScoreData={liveScoreData} />
      )}
      {commentary?.length > 0 && (
        <>
          {!isDailyHuntMode && <CommentaryFilter matchDetail={matchDetail} handleFilterChange={handleFilterChange} />}
          <Commentary data={commentary} matchDetail={matchDetail} playingXI={playingXI} />
          {loading && commentaryLoading()}
        </>
      )}
      {isLoadMoreVisible.current && (
        <div className="text-center my-3">
          <Button className="theme-btn" onClick={() => handleLoadMore()}>
            <Trans i18nKey="common:LoadMore" />
          </Button>
        </div>
      )}
      {matchDetail?.sStatusStr !== 'live' && commentary?.length === 0 && <NoData />}
    </>
  )
}
CommentaryV2.propTypes = {
  data: PropTypes.array,
  matchDetail: PropTypes.object,
  liveScoreData: PropTypes.array,
  // handleCommentary: PropTypes.func,
  playingXI: PropTypes.object,
  isDailyHuntMode: PropTypes.bool
}
export default CommentaryV2
